// src/pages/RendezVous.jsx
// Page publique /rendez-vous : demande de rendez-vous confidentiel (Art Basel Paris 2026).
// Même gabarit que /notaires et /cercle-experts. Envoie à la fonction rdv-request.
// Règle : aucune question sur le contenu ou la valeur de la collection.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { track, getAnonId } from "@/lib/track";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const PROFILES = [
  ["collector", "Collectionneur"],
  ["family_office", "Family office"],
  ["notary", "Notaire"],
  ["expert", "Expert"],
  ["advisor", "Art Advisor"],
  ["other", "Autre"],
];

const KINDS = [
  ["paris", "À Paris, pendant la semaine d'Art Basel"],
  ["online", "Démonstration en ligne de 30 minutes"],
];

const EMPTY = { name: "", email: "", profile: "", kind: "", slots: "", message: "", consent: false, website: "" };

export default function RendezVous() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = `${BRAND_NAME}, demande de rendez-vous confidentiel`;
    track("rdv_page_view", { meta: { source: new URLSearchParams(window.location.search).get("utm_source") || document.referrer || null } });
  }, []);

  const field = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Merci d'indiquer votre nom et prénom."); return; }
    if (!EMAIL_RE.test(form.email.trim())) { setError("Merci d'indiquer une adresse e-mail valide."); return; }
    if (!form.profile) { setError("Merci de choisir votre profil."); return; }
    if (!form.kind) { setError("Merci de choisir le type de rendez-vous."); return; }
    if (!form.slots.trim()) { setError("Merci d'indiquer les jours et créneaux qui vous conviennent."); return; }
    if (!form.consent) { setError("Merci de cocher la case de consentement."); return; }
    setSending(true); setError(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("rdv-request", {
        body: {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          profile: form.profile,
          kind: form.kind,
          slots: form.slots.trim(),
          message: form.message.trim(),
          consent: true,
          website: form.website, // champ piège anti-robots (doit rester vide)
          brand: BRAND_NAME,
          anon_id: getAnonId(),
        },
      });
      if (err || !(data && data.ok)) throw err || new Error("request_failed");
      track("rdv_request_submit", { meta: { profile: form.profile, kind: form.kind } });
      setDone(true);
    } catch (err) {
      console.error("[rendez-vous] request:", err);
      setError("Un problème est survenu. Réessayez dans un instant, ou écrivez-nous à contact@artkiv.com.");
    } finally {
      setSending(false);
    }
  };

  const input = "px-4 py-3 border border-[#e5e0d8] rounded-lg text-base bg-white w-full";
  const label = "block text-sm text-[#4a453d] mb-1.5";

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      <header className="border-b border-[#e5e0d8]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Accueil" className="no-underline text-inherit">
            <BrandMark className="text-2xl tracking-[0.15em]" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.22em] text-[#8a8378] mb-4">Art Basel Paris · du 21 au 25 octobre 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
          Un rendez-vous privé, strictement confidentiel.
        </h1>
        <p className="text-lg text-[#4a453d] leading-relaxed mb-3">
          Un collectionneur n'a pas besoin de montrer sa collection pour la protéger.
        </p>
        <p className="text-base text-[#6b6459] leading-relaxed mb-10">
          Pendant la semaine d'Art Basel Paris, nous vous proposons un rendez-vous de présentation à Paris, autour du
          Grand Palais, ou une démonstration en ligne de 30 minutes. Les rendez-vous sont en nombre limité. Nous ne vous
          demandons aucune information sur le contenu de votre collection.
        </p>

        {done ? (
          <div className="bg-white border border-[#e5e0d8] rounded-xl p-8" role="status">
            <h2 className="text-2xl font-bold mb-3">Merci.</h2>
            <p className="text-base text-[#4a453d] leading-relaxed">
              Nous revenons vers vous rapidement pour confirmer un créneau. Un e-mail de réception vient de vous être
              envoyé : pensez à regarder vos courriers indésirables s'il n'arrive pas.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#e5e0d8] rounded-xl p-6 md:p-8 space-y-5" noValidate>
            <div>
              <label htmlFor="rdv-name" className={label}>Nom et prénom</label>
              <input id="rdv-name" className={input} value={form.name} onChange={field("name")} autoComplete="name" required />
            </div>

            <div>
              <label htmlFor="rdv-email" className={label}>Adresse e-mail</label>
              <input id="rdv-email" type="email" className={input} value={form.email} onChange={field("email")} autoComplete="email" required />
            </div>

            <fieldset>
              <legend className={label}>Votre profil</legend>
              <div className="flex flex-wrap gap-2">
                {PROFILES.map(([v, l]) => (
                  <label key={v} className={`px-4 py-2 border rounded-full text-sm cursor-pointer ${form.profile === v ? "border-[#1a1a1a] bg-[#1a1a1a] text-[#f9f7f4]" : "border-[#e5e0d8] bg-white"}`}>
                    <input type="radio" name="rdv-profile" value={v} checked={form.profile === v} onChange={field("profile")} className="sr-only" />
                    {l}
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className={label}>Type de rendez-vous</legend>
              <div className="space-y-2">
                {KINDS.map(([v, l]) => (
                  <label key={v} className="flex items-center gap-3 text-base cursor-pointer">
                    <input type="radio" name="rdv-kind" value={v} checked={form.kind === v} onChange={field("kind")} />
                    {l}
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="rdv-slots" className={label}>Jours et créneaux qui vous conviennent</label>
              <textarea id="rdv-slots" className={input} rows={3} value={form.slots} onChange={field("slots")} required
                placeholder="Par exemple : jeudi 23 en fin de matinée, ou vendredi après 15 h" />
            </div>

            <div>
              <label htmlFor="rdv-message" className={label}>Message (facultatif)</label>
              <textarea id="rdv-message" className={input} rows={3} value={form.message} onChange={field("message")} />
            </div>

            {/* Champ piège anti-robots : invisible pour les personnes, doit rester vide. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
              <label>Ne pas remplir<input tabIndex={-1} autoComplete="off" value={form.website} onChange={field("website")} /></label>
            </div>

            <label className="flex items-start gap-3 text-sm text-[#4a453d] leading-relaxed cursor-pointer">
              <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
              <span>
                J'accepte que ces informations servent à me répondre et à organiser ce rendez-vous. Elles ne sont pas
                communiquées à des tiers. Voir la <Link to="/privacy-policy" className="underline">politique de confidentialité</Link>.
              </span>
            </label>

            {error && <p role="alert" className="text-sm text-[#a33]">{error}</p>}

            <button type="submit" disabled={sending}
              className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black disabled:opacity-60">
              {sending ? "Envoi…" : "Demander un rendez-vous"}
            </button>
            <p className="text-xs text-[#8a8378]">
              Vos informations restent strictement confidentielles et servent uniquement à vous répondre.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
